import {useGetInfinitePatients} from '@/modules/patients/api/use-get-infinite-patients'
import {PatientsFilters} from '@/modules/patients/components/patients-filters'
import {PatientsTable} from '@/modules/patients/components/patients-table/patients-table'
import {PatientSearchSchema} from '@/modules/patients/schemas'
import {Stack} from '@chakra-ui/react'
import {PatientSource} from '@cypro/engine/database/schema'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {toaster} from '@cypro/ui/toaster'
import {createFileRoute} from '@tanstack/react-router'
import {OnChangeFn, SortingState} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {useCallback, useEffect, useMemo} from 'react'
import {LuUserRoundSearch} from 'react-icons/lu'

export const Route = createFileRoute('/patient-dashboard/patients/')({
  component: PatientsPage,
  validateSearch: PatientSearchSchema,
})

const LIMIT = 50

function PatientsPage() {
  const {t} = useTranslate('patients')
  const {t: tCommon} = useTranslate('common')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // A route guard that refuses access redirects here with `forbidden`, so explain the bounce once.
  useEffect(() => {
    if (!search.forbidden) return
    toaster.info({title: tCommon('no_access')})
    navigate({to: '.', search: prev => ({...prev, forbidden: undefined}), replace: true})
  }, [navigate, search.forbidden, tCommon])

  const shouldDisplayMainContent = useMemo(() => {
    const isSearchingHl7Patients = search.filters?.source?.includes(PatientSource.enum.hl7_message)
    const hasNameFilter = Boolean(search.filters?.search)
    return !isSearchingHl7Patients || (isSearchingHl7Patients && hasNameFilter)
  }, [search.filters?.source, search.filters?.search])

  return (
    <>
      <PatientsFilters />

      {!shouldDisplayMainContent && (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <EmptyState
            title={t('search_hl7_database.title')}
            description={t('search_hl7_database.description')}
            icon={LuUserRoundSearch}
            maxWidth="md"
          />
        </Stack>
      )}

      {shouldDisplayMainContent && <MainContent />}
    </>
  )
}

function MainContent() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const [sort] = search.sort

  const infinitePatients = useGetInfinitePatients({
    limit: LIMIT,
    sort: {
      dir: sort?.desc ? 'desc' : 'asc',
      field: sort?.id || 'createdAt',
    },
    filters: {
      name: search.filters?.search,
      responsibleDoctor: search.filters?.responsibleDoctor,
      source: search.filters?.source,
    },
  })

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = infinitePatients

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    updaterOrValue => {
      const newValue = typeof updaterOrValue === 'function' ? updaterOrValue(search.sort) : updaterOrValue
      navigate({to: '.', search: prev => ({...prev, sort: newValue})})
    },
    [navigate, search.sort]
  )

  return (
    <PatientsTable
      virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
      table={{onSortingChange, state: {sorting: search.sort}}}
      source={search.filters?.source}
    />
  )
}
